<template>
  <div class="us-root">
    <!-- 只读触发区：已选标签 + 点击唤起全屏选人 -->
    <div
      class="mus-trigger"
      :class="{ 'is-disabled': disabled }"
      @click="openPicker"
    >
      <span
        v-for="(name, idx) in selectedNames"
        :key="idx"
        class="mus-tag"
      >
        {{ tagText(name) }}
        <van-icon
          v-if="!disabled"
          name="cross"
          class="mus-tag-close"
          @click.stop="removeName(idx)"
        />
      </span>
      <span v-if="!selectedNames.length" class="mus-placeholder">
        {{ placeholder || '请选择人员' }}
      </span>
        <van-icon
          v-if="!disabled"
          name="arrow"
          class="mus-arrow"
        />
        <!-- 对齐 ifweb 的 clearable：有值且未禁用时显示一键清空 -->
        <van-icon
          v-if="clearable && !disabled && selectedNames.length"
          name="clear"
          class="mus-clear"
          @click.stop="onClear"
        />
      </div>

    <van-popup
      v-model="popupVisible"
      position="bottom"
      get-container="body"
      round
      class="mus-popup"
      :style="{ width: '100%', height: '50%' }"
    >
      <div class="mus-page">
        <div class="mus-header">
          <van-search
            v-model="keyword"
            class="mus-search"
            :placeholder="inputPlaceholder"
            show-action
            @input="onKeywordInput"
            @search="doSearch"
            @paste.native="onPaste"
          >
            <template #action>
              <div @click="doSearch">搜索</div>
            </template>
          </van-search>
          <van-tabs v-model="activeTab" class="mus-tabs" @change="onTabChange">
            <van-tab
              v-for="tab in visibleTabs"
              :key="tab.name"
              :title="tab.title"
              :name="tab.name"
            ></van-tab>
          </van-tabs>
        </div>

        <div class="mus-body">
          <van-loading v-if="searchLoading" class="mus-loading" size="20px">
            搜索中...
          </van-loading>

          <template v-else-if="mobileList.length">
            <!-- 多选 -->
            <van-checkbox-group v-if="multiple" v-model="checkedNames">
              <div
                v-for="item in mobileList"
                :key="item.ck"
                class="mus-item"
              >
                <van-checkbox :name="item.ck" class="mus-check">
                  <div class="mus-item-main">
                    <div class="mus-item-name">
                      {{ item.label }}
                      <span v-if="orgText(item)" class="mus-item-org">{{ orgText(item) }}</span>
                    </div>
                    <div class="mus-item-sub">{{ item.name }}</div>
                  </div>
                </van-checkbox>
              </div>
            </van-checkbox-group>

            <!-- 单选 -->
            <div
              v-for="item in mobileList"
              v-else
              :key="item.ck"
              class="mus-item mus-item-single"
              @click="toggleSingle(item)"
            >
              <div class="mus-item-main">
                <div class="mus-item-name">
                  {{ item.label }}
                  <span v-if="orgText(item)" class="mus-item-org">{{ orgText(item) }}</span>
                </div>
                <div class="mus-item-sub">{{ item.name }}</div>
              </div>
              <van-icon
                v-if="checkedNames.includes(item.ck)"
                name="success"
                class="mus-checked"
              />
            </div>
          </template>

          <div v-else class="mus-empty">
            {{ keyword ? '未找到匹配人员' : inputPlaceholder }}
          </div>
        </div>

        <div class="mus-footer">
          <div class="mus-count">已选 {{ checkedNames.length }} 人</div>
          <div class="mus-actions">
            <van-button
              plain
              size="small"
              class="mus-btn"
              @click="popupVisible = false"
            >
              关闭
            </van-button>
            <van-button
              type="info"
              size="small"
              class="mus-btn"
              @click="confirm"
            >
              确定
            </van-button>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import {
  Search,
  Popup,
  Tab,
  Tabs,
  Checkbox,
  CheckboxGroup,
  Icon,
  Button,
  Loading,
  Dialog,
} from 'vant';
import { mapGetters } from 'vuex';
import { newSearchStaffList } from '@/api/person/personApi';
import { formatApi } from '@/api/baseApi';

/**
 * 请求优化（对齐 ifweb personApiOptimized.js + apiCache.js）
 * 同一 关键词+企业 的并发请求自动合并为一次，结果全局缓存 5 分钟。
 * 组件卸载不会丢失缓存，多个 UserSelect 实例之间共享。
 */
const CACHE_EXPIRY = 5 * 60 * 1000; // 与 ifweb 一致：5 分钟
const apiCache = new Map();
const pendingMap = new Map();

function cacheKeyOf(name, args) {
  return `${name}_${JSON.stringify(args)}`;
}

function withOptimize(name, fn) {
  return function optimized(...args) {
    const key = cacheKeyOf(name, args);
    const hit = apiCache.get(key);
    // 命中未过期缓存直接返回，不发请求
    if (hit && Date.now() - hit.timestamp < CACHE_EXPIRY) {
      return Promise.resolve(hit.data);
    }
    if (hit) apiCache.delete(key);
    // 相同参数的并发请求合并为一次，只发一个网络请求
    if (pendingMap.has(key)) return pendingMap.get(key);
    const task = fn(...args)
      .then((data) => {
        apiCache.set(key, { data, timestamp: Date.now() });
        return data;
      })
      .finally(() => {
        pendingMap.delete(key);
      });
    pendingMap.set(key, task);
    return task;
  };
}

// 后端暂不支持按 type 过滤，放大条数后在前端按 Tab 过滤
const SEARCH_LIMIT = 100;

/**
 * 默认 Tab（当前用户所属企业识别不出来时用）。
 */
const DEFAULT_TAB = 'od';

/**
 * 企微 corpID(appid) 后缀 -> Tab name 的映射。
 *
 * idc科技、犀牛鸟账号登录时，人员选择器默认切到自己所属的企业，
 * 免去每次手动切 Tab。判定依据由产品侧给定：
 *   appid 以 125 结尾 -> 腾讯IDC科技
 *   appid 以 dff 结尾 -> 犀牛鸟
 * 其余（含腾讯 OA、取不到 appid）一律回落 DEFAULT_TAB。
 *
 * 注意后缀要小写比较：corpID 里含大小写字母，实测同一企业在不同
 * 入口下大小写并不统一。
 */
const APPID_SUFFIX_TAB_MAP = [
  { suffix: '125', tab: 'od' },
  { suffix: 'dff', tab: 'xnn' },
];

/**
 * 依据 sessionStorage 里的 appid（企微 corpID）判断当前用户所属企业对应的 Tab。
 *
 * @returns {String} Tab name：'oa' | 'od' | 'xnn'
 */
function detectCurrentCompanyTab() {
  let appid = '';
  try {
    appid = sessionStorage.getItem('appid') || '';
  } catch (e) {
    // 隐私模式等场景下访问 sessionStorage 可能抛错，按取不到处理
    appid = '';
  }
  if (!appid) return DEFAULT_TAB;

  const normalized = String(appid).trim().toLowerCase();
  const hit = APPID_SUFFIX_TAB_MAP.find((item) => normalized.endsWith(item.suffix));
  return hit ? hit.tab : DEFAULT_TAB;
}

// 对齐 ifweb newUserSelect：按企业（公司）搜索人员
// wechat-web 的 personApi 未提供该方法，这里按 ifweb 的
// Action=Tof / Method=StaffIDCInfoKeyValue 契约本地实现
async function newSearchCompatibleList(keyword, company, limit, enableTencentStaffName) {
  const result = await formatApi({
    Action: 'Tof',
    Method: 'StaffIDCInfoKeyValue',
    SystemId: '2',
    Keyword: keyword,
    IsOnlyIDC: true,
    Company: company,
    Limit: parseInt(limit, 10),
    ...(enableTencentStaffName === true ? { TencentStaffName: sessionStorage.getItem('userid') } : {}),
  });
  if (!result) throw new Error('empty response');
  const { data } = result;
  // 接口不可用时后端不会抛错，而是返回非 0 code / 带 ErrInfo，需显式识别
  if (result.code !== undefined && result.code !== 0) {
    throw new Error((data && data.ErrInfo) || 'StaffIDCInfoKeyValue unavailable');
  }
  // 兼容 { data: [...] } 与 { data: { StaffInfo: [...] } } 两种结构
  const raw = Array.isArray(data)
    ? data
    : (data && (data.StaffInfo || data.data)) || [];
  return Array.isArray(raw) ? raw : [];
}

// 开启请求优化：缓存 5 分钟 + 并发合并 + 跨实例共享
const optimizedStaffList = withOptimize(
  'newSearchStaffList',
  (...args) => newSearchStaffList(...args)
);
const optimizedCompatibleList = withOptimize(
  'newSearchCompatibleList',
  (...args) => newSearchCompatibleList(...args)
);

export default {
  name: 'UserSelect',
  components: {
    [Search.name]: Search,
    [Popup.name]: Popup,
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
    [Checkbox.name]: Checkbox,
    [CheckboxGroup.name]: CheckboxGroup,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Loading.name]: Loading,
  },
  model: {
    prop: 'selected',
    event: 'user-select-change',
  },
  props: {
    selected: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    showFullData: {
      type: Boolean,
      default: false,
    },
    fullData: {
      type: Array,
      default: () => [],
    },
    // 是否仅限腾讯 OA 用户（对齐 ifweb：锁定为腾讯，隐藏企业切换）
    oaOnly: {
      type: Boolean,
      default: false,
    },
    // 是否走新版搜索接口（对齐 ifweb newVersion）
    newVersion: {
      type: Boolean,
      default: false,
    },
    // PC 时代遗留 prop，移动端无对应交互，保留声明仅为兼容调用方
    allowCreate: {
      type: Boolean,
      default: false,
    },
    // 是否支持批量粘贴（对齐 ifweb onPaste：按 , ; 空格 拆分多人）
    allowPaste: {
      type: Boolean,
      default: false,
    },
    // 是否允许一键清空（对齐 ifweb el-select 的 clearable）
    clearable: {
      type: Boolean,
      default: false,
    },
    // 请求优化开关（对齐配置面板「请求优化」）：
    // 开启 -> 走 withOptimize 缓存层（5 分钟 TTL + 并发合并 + 跨实例共享）
    // 关闭 -> 每次都直接发真实请求，不做任何缓存
    requestOptimize: {
      type: Boolean,
      default: false,
    },
    // 是否传递腾讯员工ID（对齐 ifweb：开启后搜索请求携带 TencentStaffName 字段，值为当前登录用户id）
    enableTencentStaffName: {
      type: Boolean,
      default: false,
    },
    // 转单等场景：目标账号包含腾讯 OA 账号时，确定前弹框二次确认。
    // 默认关闭 —— 筛选、表格编辑等场景选中 OA 人员是常态，无需确认
    confirmOaAccount: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchLoading: false,
      rawUserList: [],
      userMap: {},
      popupVisible: false,
      keyword: '',
      // 默认停在当前用户所属企业（由 appid 后缀判定），
      // 让 idc科技 / 犀牛鸟 账号打开即是自己的企业，不用每次手动切
      activeTab: detectCurrentCompanyTab(),
      checkedNames: [],
      searchTimer: null,
      tabTimer: null,
      searchSeq: 0,
      // 复选框唯一 key -> 姓名(value) 的映射，用于回写选中值
      keyNameMap: {},
      // 唯一 key -> { label, company, tabName }，用于已选标签展示企业后缀
      ckInfoMap: {},
      // 姓名(value) -> 唯一 key，记录用户实际勾选的是哪家企业，
      // 解决同 value 多企业时（philyuliu 既是腾讯又是IDC）回显错配成腾讯的问题
      // 已选项位置索引 -> 唯一 key(姓名__企业)，与 selectedNames 一一对应。
      // 同姓名不同企业（如 philyuliu 的腾讯/IDC）按位置分别记录，
      // 不能再用「姓名 -> key」的映射，否则同名项会互相覆盖成最后勾的那个
      selectedCkList: [],
      // 后端企业接口不可用时降级，此后统一走 StaffInfoKeyValue
      useCompatible: true,
      TYPE_MAP: {
        oa: '@腾讯',
        od: '@腾讯IDC科技',
        xnn: '@犀牛鸟',
      },
      // 只保留 3 个具体企业。
      // 原先还有一个「全部」Tab，已按产品要求移除：它在前端不做任何过滤
      // （直接返回 rawUserList），同名不同企业的人会并排出现、难以分辨，
      // 且后端 Company 传「全部」并非有效企业名，过滤口径也不明确。
      TAB_LIST: [
        { name: 'oa', title: '@腾讯', company: '腾讯' },
        { name: 'od', title: '@腾讯IDC科技', company: '腾讯IDC科技' },
        { name: 'xnn', title: '@犀牛鸟', company: '犀牛鸟有限责任公司' },
      ],
    };
  },
  watch: {
    selected(newVal) {
      if (this.showFullData && newVal.length) {
        this.getUserData();
      }
    },
  },
  computed: {
    selectedNames() {
      return this.selected
        ? this.selected.split(';').filter((name) => name)
        : [];
    },
    // 搜索框与空状态提示共用同一份文案，避免两处各写一份导致不一致
    inputPlaceholder() {
      return this.placeholder || '姓名或企微ID搜索';
    },
    // oaOnly 时对齐 ifweb：强制锁定为腾讯，不展示其它企业
    visibleTabs() {
      if (this.oaOnly) {
        return this.TAB_LIST.filter((tab) => tab.name === 'oa');
      }
      return this.TAB_LIST;
    },
    // 对齐 ifweb 的 companyName：当前 Tab 对应的企业全名。
    // activeTab 恒为三家企业之一，取不到时回落默认 Tab 的企业名，
    // 不能再回落成「全部」——它已不是合法 Tab，传给后端也没有过滤意义
    currentCompany() {
      const hit = this.TAB_LIST.find((tab) => tab.name === this.activeTab)
        || this.TAB_LIST.find((tab) => tab.name === DEFAULT_TAB)
        || this.TAB_LIST[0];
      return hit ? hit.company : '';
    },
    // 对齐 ifweb newUserSelect：按 company(企业全名) 筛选，
    // 不用 type —— 实测后端 type 会错配（犀牛鸟返回 od）
    mobileList() {
      const target = this.currentCompany;
      return this.rawUserList.filter((item) => {
        const company = item.company || item.accountType || '';
        // 无法识别企业时保留，避免后端未返回 company 导致列表被清空
        if (!company) return true;
        // 必须精确匹配：「腾讯」不能把「腾讯IDC科技」也包含进来
        return company === target;
      });
    },
  },
  async mounted() {
    if (this.showFullData) {
      this.getUserData();
    }
    if (this.fullData?.length) {
      this.setRawList(this.normalize(this.fullData));
    }
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.tabTimer) clearTimeout(this.tabTimer);
  },
  methods: {
    // 归一到 Tab name。company 是权威字段，type 仅作兜底（实测 type 可能错配）
    typeOf(company, type) {
      const tabs = this.TAB_LIST;
      const match = (val) => {
        if (!val) return null;
        const raw = String(val);
        // 先按 name 精确匹配（oa / od / xnn）
        const exact = tabs.find((tab) => tab.name === raw);
        if (exact) return exact.name;
        // 再按企业名模糊匹配，长名优先，避免"腾讯IDC科技"被"腾讯"抢先命中
        const sorted = [...tabs].sort(
          (a, b) => b.company.length - a.company.length
        );
        const hit = sorted.find(
          (tab) =>
            raw.includes(tab.company) ||
            raw.includes(tab.title.replace('@', ''))
        );
        return hit ? hit.name : null;
      };
      return match(company) || match(type);
    },

    normalize(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => {
        // 实测后端 type 不可靠（犀牛鸟返回的是 od），
        // 以 company/accountType 为企业识别的权威字段，type 仅作兜底
        const accountType = item.accountType || '';
        const company = item.company || accountType;
        const tabName = this.typeOf(company, item.type);
        return {
          name: item.value,
          label: item.label,
          type: item.type || '',
          // tabName 由 company 归一而来，专供展示用；筛选一律以 company 为准
          tabName: tabName || '',
          company,
          accountType,
          // 同 value 不同企业时 name 相同，复选框会连带勾选，
          // 故用 姓名__企业 作为唯一标识（对齐 ifweb uniqueKey 思路）
          ck:
            item.value +
            (company ? `__${company}` : ''),
          fullName: item.value.startsWith('C_')
            ? item.label
            : item.label + (this.TYPE_MAP[tabName] || ''),
        };
      });
    },

    // 统一写入列表，同时维护回显用的 userMap 与唯一 key 映射
    setRawList(list) {
      this.rawUserList = list;
      const map = { ...this.userMap };
      const keyMap = { ...this.keyNameMap };
      const infoMap = { ...this.ckInfoMap };
      list.forEach((item) => {
        map[item.name] = item.label;
        keyMap[item.ck] = item.name;
        infoMap[item.ck] = {
          label: item.label,
          company: item.company,
          tabName: item.tabName,
        };
      });
      this.userMap = map;
      this.keyNameMap = keyMap;
      this.ckInfoMap = infoMap;
    },

    orgText(item) {
      // 用 company 归一后的 tabName 取后缀，避免后端 type 错配导致显示错误
      return this.TYPE_MAP[item.tabName] || '';
    },

    // 已选标签文本：对齐 ifweb PC newUserSelect —— 同一姓名(value)对应多家企业时，
    // 合并所有企业用 && 连接，如 李祥龙(腾讯&&腾讯IDC科技)，
    // 而不是只显示最后勾选的那一家
    tagText(name) {
      const label = this.userMap[name] || name;
      // 收集该姓名对应的所有企业：已勾选记录优先，其次当前列表与全局映射
      const companySet = new Set();
      const pushCompany = (ck) => {
        const info = this.ckInfoMap[ck];
        if (info && info.company) companySet.add(info.company);
      };
      // 1. 用户实际勾选过的所有位置（同名多选时会有多个）
      this.selectedCkList.forEach((ck) => {
        if (this.keyNameMap[ck] === name) pushCompany(ck);
      });
      // 2. 已知数据中该姓名对应的其它企业
      this.ckListByName(name).forEach(pushCompany);
      const companies = Array.from(companySet).filter(Boolean);
      // C_ 开头是企业账号，无论是否同名都要带企业后缀，否则看不出是哪家
      const isCompanyAccount = String(name).startsWith('C_');
      // 只有一家企业时不加后缀，避免噪音
      if (!isCompanyAccount && companies.length <= 1) {
        return label;
      }
      // 对齐 PC 的 (A&&B) 形式：PC 用括号包裹，此处沿用
      return companies.length
        ? `${label}(${companies.join('&&')})`
        : label;
    },

    // 同一姓名(value)对应的所有唯一 key，用于判断是否需要带企业后缀
    ckListByName(name) {
      const set = new Set();
      this.rawUserList.forEach((item) => {
        if (item.name === name) set.add(item.ck);
      });
      Object.keys(this.keyNameMap).forEach((ck) => {
        if (this.keyNameMap[ck] === name) set.add(ck);
      });
      return Array.from(set);
    },

    openPicker() {
      if (this.disabled) return;
      // 已选值是姓名，需补全为 姓名__企业 的唯一 key 才能匹配复选框。
      // 按位置还原，保证同名不同企业的两项各自勾到自己那家
      this.checkedNames = this.selectedNames.map(
        (name, i) => this.nameToCk(name, i) || name
      );
      // oaOnly 时锁定腾讯；否则每次打开都回到「当前用户所属企业」，
      // 而不是沿用上次切换的 Tab —— 保证默认态始终可预期
      this.activeTab = this.oaOnly ? 'oa' : detectCurrentCompanyTab();
      this.popupVisible = true;
    },

    // 对齐 ifweb：切换企业即携带 Company 重新请求（后端过滤）。
    // 缓存统一由全局 withOptimize 处理（5 分钟 TTL + 并发合并），此处不再重复缓存
    onTabChange() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (this.tabTimer) clearTimeout(this.tabTimer);
      this.tabTimer = setTimeout(() => {
        this.doSearch();
      }, 60);
    },

    // 由姓名反查唯一 key。indexOf 是该姓名在已选项中的位置，
    // 同名多企业时靠它区分：先查用户实际勾选记录，再用当前列表/全局映射兜底
    nameToCk(name, indexOf = -1) {
      if (!name) return '';
      // 1. 该位置上用户实际勾选过 -> 直接用他选的那家企业
      if (indexOf >= 0 && this.selectedCkList[indexOf]) {
        return this.selectedCkList[indexOf];
      }
      const cks = this.ckListByName(name);
      // 2. 当前列表中找
      const hit = this.rawUserList.find((item) => item.name === name);
      if (hit) return hit.ck;
      // 3. 全局映射兜底
      return cks[0] || name;
    },

    onKeywordInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.doSearch();
      }, 300);
    },

    // 按「请求优化」开关分派：开启走缓存层（5 分钟 TTL + 并发合并），关闭则直连
    searchStaffApi(kw) {
      return this.requestOptimize
        ? optimizedStaffList(kw, SEARCH_LIMIT, this.enableTencentStaffName)
        : newSearchStaffList(kw, SEARCH_LIMIT, this.enableTencentStaffName);
    },

    searchCompatibleApi(kw, company) {
      return this.requestOptimize
        ? optimizedCompatibleList(kw, company, SEARCH_LIMIT, this.enableTencentStaffName)
        : newSearchCompatibleList(kw, company, SEARCH_LIMIT, this.enableTencentStaffName);
    },

    async doSearch() {
      const kw = (this.keyword || '').trim();
      if (!kw) {
        this.setRawList([]);
        return;
      }
      // 递增请求序号，丢弃过期响应，避免慢请求覆盖新结果
      const seq = (this.searchSeq || 0) + 1;
      this.searchSeq = seq;
      this.searchLoading = true;
      try {
        let staffNameList;
        const fallbackSearch = () => this.searchStaffApi(kw);
        if (this.useCompatible) {
          try {
            // 对齐 ifweb：把当前企业作为 Company 交给后端过滤
            staffNameList = await this.searchCompatibleApi(
              kw,
              this.currentCompany
            );
          } catch (e) {
            // 企业接口不可用则永久降级，后续不再尝试，避免每个 Tab 都失败一次
            this.useCompatible = false;
            staffNameList = await fallbackSearch();
          }
        } else {
          staffNameList = await fallbackSearch();
        }
        if (seq !== this.searchSeq) return;
        // 缓存与并发合并已由全局 withOptimize 统一处理，此处只做归一
        this.setRawList(this.normalize(staffNameList || []));
      } catch (e) {
        if (seq !== this.searchSeq) return;
        this.setRawList([]);
      } finally {
        // 无论成功失败都复位，避免一直卡在"搜索中"
        if (seq === this.searchSeq) this.searchLoading = false;
      }
    },

    toggleSingle(item) {
      this.checkedNames = [item.ck];
      this.confirm();
    },

    // 对齐 ifweb onPaste：支持 , ; 空格 分隔的多人批量粘贴
    onPaste(event) {
      if (!this.allowPaste) return;
      const clipboard = event.clipboardData || window.clipboardData;
      if (!clipboard) return;
      event.preventDefault();
      let pasteText = clipboard.getData('text') || '';
      // 与 ifweb 完全相同的清洗：分隔符统一为 ; 去掉首尾 ; 剥离括号内容
      pasteText = pasteText
        .replace(/[,;\s]+/g, ';')
        .replace(/^;|;$/g, '')
        .replace(/\([\W]+\)/g, '');
      const items = pasteText.split(';').filter((name) => name);
      if (!items.length) return;
      if (this.multiple) {
        // 多选：与已选合并去重（对齐 ifweb 的 Set 去重）
        const merged = Array.from(
          new Set([...this.selectedNames, ...items])
        );
        this.emitChange(merged.join(';'));
      } else {
        // 单选：取第一个并立即搜索回显（对齐 ifweb）
        this.keyword = items[0];
        this.emitChange(items[0]);
        this.doSearch();
      }
    },

    // 对齐 ifweb 的 clearable：一键清空已选
    onClear() {
      if (this.disabled) return;
      this.checkedNames = [];
      this.selectedCkList = [];
      this.emitChange('');
    },

    removeName(index) {
      const next = this.selectedNames.filter((n, i) => i !== index).join(';');
      // 同步移除该位置记录的企业，避免后续位置整体前移导致错配
      this.selectedCkList = this.selectedCkList.filter((ck, i) => i !== index);
      this.emitChange(next);
    },

    async confirm() {
      // 对外值按姓名去重，保留首次出现的那一项，
      // 避免同名不同企业（philyuliu 腾讯/IDC）被提交成 philyuliu;philyuliu
      const seenName = new Set();
      const keepCks = [];
      this.checkedNames.forEach((ck) => {
        const name = this.keyNameMap[ck] || ck;
        if (!name || seenName.has(name)) return;
        seenName.add(name);
        keepCks.push(ck);
      });
      // checkedNames 存的是唯一 key，对外仍输出姓名(value)
      const names = keepCks
        .map((ck) => this.keyNameMap[ck] || ck)
        .filter((n) => n);
      const result = this.multiple ? names.join(';') : names[0] || '';
      // 转单等场景（confirmOaAccount）：目标账号含腾讯 OA 账号时先二次确认。
      // 取消时不写 selectedCkList、不关闭弹层，保留原已选值方便改选
      if (this.confirmOaAccount) {
        const oaNames = this.oaNamesOf(keepCks);
        if (oaNames.length) {
          const confirmed = await this.confirmOaTransfer(oaNames);
          if (!confirmed) return;
        }
      }
      // 勾选顺序即列表顺序，按位置一一记住每项的 key，
      // 同名不同企业时（腾讯/IDC）才能各自回显，不会被最后一个覆盖
      this.selectedCkList = keepCks;
      this.popupVisible = false;
      this.emitChange(result);
    },

    // 唯一 key 形如「姓名__企业」，企业名直接编码在里面。
    // ckInfoMap 只覆盖本次搜索过的列表，对仅由 v-model 回显的已选项
    // 在这里从 ck 兜底解析，保证企业归属仍可判定
    companyOfCk(ck) {
      const raw = String(ck || '');
      const idx = raw.lastIndexOf('__');
      return idx > -1 ? raw.slice(idx + 2) : '';
    },

    // 从选中项中挑出属于腾讯 OA 的姓名（供转单二次确认提示用）。
    // 企业以 company 为准再经 typeOf 归一（后端 type 实测会错配）；
    // 取不到企业归属时不做判定，避免误弹确认框
    oaNamesOf(cks) {
      return (cks || [])
        .filter((ck) => {
          const info = this.ckInfoMap[ck];
          const company = (info && info.company) || this.companyOfCk(ck);
          const tabName = (info && info.tabName) || this.typeOf(company);
          return tabName === 'oa';
        })
        .map((ck) => {
          const info = this.ckInfoMap[ck];
          return (info && info.label) || this.keyNameMap[ck] || ck;
        });
    },

    // 目标账号含腾讯 OA 时的二次确认弹框，返回 Promise<Boolean>（确认 true / 取消 false）。
    // Dialog 默认 confirm 模式，点遮罩不关闭，必须显式选择
    confirmOaTransfer(oaNames) {
      return new Promise((resolve) => {
        Dialog.confirm({
          title: '转单确认',
          message: `目标账号中包含腾讯账号：${oaNames.join('、')}，确认转单给该账号吗？`,
          confirmButtonText: '确认转单',
          cancelButtonText: '取消',
        })
          .then(() => resolve(true))
          .catch(() => resolve(false));
      });
    },

    emitChange(result) {
      const next = (result || '')
        .split(';')
        .filter((name) => name)
        .join(';');
      this.$emit('user-select-change', next);
      this.$emit('child-click', next);
    },

    // showFullData 时按已选值反查姓名，用于标签回显
    async getUserData() {
      try {
        const staffNameList = await newSearchStaffList(this.selected, 100, this.enableTencentStaffName);
        this.setRawList(this.normalize(staffNameList));
      } catch (e) {}
    },
  },
};
</script>

<style lang="less">
.us-root {
  width: 100%;
}

.mus-trigger {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 32px;
  padding: 3px 24px 3px 0;
  position: relative;
  background: transparent;
  border: none;
  box-sizing: border-box;

  &.is-disabled {
    background: transparent;
    color: #c0c4cc;
    cursor: not-allowed;
  }
}

.mus-placeholder {
  color: #c0c4cc;
  font-size: 13px;
  line-height: 20px;
}

.mus-arrow {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  font-size: 13px;
}

// 清空按钮：位于箭头左侧，避免与箭头重叠
.mus-clear {
  position: absolute;
  right: 26px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  font-size: 14px;
  z-index: 1;
}

.mus-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 1px 6px;
  font-size: 12px;
  line-height: 18px;
  color: #1989fa;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 3px;
}

.mus-tag-close {
  margin-left: 4px;
  font-size: 11px;
  color: #a0cfff;
}

.mus-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f8fa;
  overflow: hidden;
}

.mus-header {
  flex: none;
  background: #fff;
}

.mus-search {
  padding: 8px 12px 4px;

  /deep/ .van-search__content {
    background: #f7f8fa;
    border-radius: 16px;
  }

  /deep/ .van-field__control {
    font-size: 14px;
  }
}

.mus-tabs {
  border-bottom: 1px solid #ebedf0;

  /deep/ .van-tab {
    font-size: 13px;
  }

  /deep/ .van-tabs__nav {
    padding-bottom: 0;
  }
}

.mus-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
}

.mus-loading,
.mus-empty {
  padding: 24px 0;
  text-align: center;
  color: #969799;
  font-size: 13px;
}

.mus-item {
  padding: 8px 16px;
  border-bottom: 1px solid #f2f3f5;
}

.mus-item-single {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mus-check {
  width: 100%;
}

.mus-item-main {
  min-width: 0;
}

.mus-item-name {
  font-size: 14px;
  color: #323233;
  line-height: 20px;
}

.mus-item-org {
  margin-left: 6px;
  font-size: 12px;
  color: #07c160;
}

.mus-item-sub {
  font-size: 12px;
  color: #969799;
  line-height: 16px;
}

.mus-checked {
  color: #1989fa;
  font-size: 16px;
}

.mus-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px;
  background: #fff;
  border-top: 1px solid #ebedf0;
  padding-bottom: calc(8px + constant(safe-area-inset-bottom));
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}

.mus-count {
  flex: none;
  font-size: 12px;
  color: #646566;
}

.mus-actions {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.mus-btn {
  flex: none;
  height: 32px;
  line-height: 30px;
  padding: 0 16px;
  font-size: 14px;
  white-space: nowrap;
}
</style>
